import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import { Base_URL } from "../Client/apiURL";

function getAttemptId(attempt) {
  return attempt?.MockTestAttemptId || attempt?.mock_test_attempt_id || attempt?.id || attempt?.MockTestAttemptedId;
}

function getMockTestName(attempt) {
  return attempt?.MockTestName || attempt?.mock_test_name || attempt?.TestName || `Mock attempt ${getAttemptId(attempt) || ""}`;
}

function getMockType(attempt) {
  if (attempt?.Core === true || attempt?.core === true) return "pte core";
  return "pte academic";
}

function getPteRoute(attempt, studentId) {
  const attemptId = getAttemptId(attempt);
  const testType = String(attempt?.TestType || attempt?.test_type || "Full").toLowerCase();
  const routeType = testType === "full" ? "full" : "sectional";
  return `/mt-score-${routeType}/${studentId}/${attemptId}/${encodeURIComponent(getMockType(attempt))}`;
}

function getIeltsRoute(test, studentId) {
  const fullAttemptId =
    test?.latest_full_attempt_summary?.full_test_attempt_id ||
    test?.fullTestAttemptId ||
    test?.full_test_attempt_id;
  if (fullAttemptId) {
    return `/ielts-test-details?user=teacher&type=full&studentId=${studentId}&fullTestAttemptId=${fullAttemptId}`;
  }

  const attempt = test?.attempts_summary?.[0] || test?.attempts?.[0] || null;
  const attemptId = attempt?.id || attempt?.attemptId || attempt?.attempt_id || test?.attempt_id;
  if (attemptId) {
    return `/ielts-test-details?user=teacher&type=individual&studentId=${studentId}&attemptId=${attemptId}`;
  }

  const groupId = test?.groupId || test?.group_id || test?.groupid;
  if (groupId) {
    return `/ielts-test-details?user=teacher&type=group&studentId=${studentId}&groupId=${groupId}`;
  }

  return null;
}

function getIeltsScore(test) {
  const attempt = test?.latest_full_attempt_summary || test?.attempts_summary?.[0] || test?.attempts?.[0] || {};
  return attempt?.totalMarksObtained || attempt?.total_marks_obtained || attempt?.overallBandScore || attempt?.score || "-";
}

function openResult(route) {
  if (!route) return;
  window.open(route, "_blank", "noopener,noreferrer");
}

const TeacherStudentResults = () => {
  const { studentId, resultType } = useParams();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const normalizedType = String(resultType || "").toLowerCase();

  useEffect(() => {
    const fetchOverview = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${Base_URL}/app/users/teacher-access/students/${studentId}/overview`,
          { withCredentials: true }
        );
        if (response.data.responseCode === 200) {
          setOverview(response.data.response);
        } else {
          setError(response.data.message || "Could not load student results.");
        }
      } catch (requestError) {
        setError("Could not load student results.");
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [studentId]);

  const ieltsTests = useMemo(() => overview?.ielts?.response?.tests || [], [overview]);
  const pteAttempts = useMemo(() => overview?.pteAttempts || [], [overview]);
  const studentName = overview?.student?.name || `Student ${studentId}`;

  const title = normalizedType === "pte" ? "PTE Results" : "IELTS Results";

  return (
    <>
      <Navbar />
      <Box sx={{ width: "92%", maxWidth: 1180, mx: "auto", pt: { xs: 11, md: 14 }, pb: 5 }}>
        <Stack spacing={2.5}>
          <Box>
            <Button onClick={() => navigate("/teacher-mode")} sx={{ textTransform: "none", mb: 1 }}>
              Back to Teacher Mode
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 900, color: "#241B3A" }}>
              {title}
            </Typography>
            <Typography sx={{ color: "#6B6478", mt: 0.5 }}>{studentName}</Typography>
          </Box>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && normalizedType === "ielts" && (
            <>
              {ieltsTests.length === 0 && <Alert severity="info">No IELTS results found for this student.</Alert>}
              <Grid container spacing={2}>
                {ieltsTests.map((test, index) => {
                  const route = getIeltsRoute(test, studentId);
                  return (
                    <Grid item xs={12} md={6} key={`${test.id || test.groupId || index}-${index}`}>
                      <Card sx={{ borderRadius: 2, height: "100%", boxShadow: "0 12px 32px rgba(35,25,66,0.08)" }}>
                        <CardContent>
                          <Stack spacing={1.2}>
                            <Typography sx={{ fontWeight: 850 }}>
                              {test.testName || test.test_name || test.groupName || test.group_name || "IELTS test"}
                            </Typography>
                            <Typography sx={{ color: "#6B6478", fontSize: 13 }}>
                              {test.categoryName || test.category_name || test.testType || test.test_type || "IELTS"}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={`Score: ${getIeltsScore(test)}`} />
                              {test.latest_full_attempt_summary?.status && (
                                <Chip size="small" variant="outlined" label={test.latest_full_attempt_summary.status} />
                              )}
                            </Stack>
                            <Button variant="contained" disabled={!route} onClick={() => openResult(route)}>
                              Open
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {!loading && !error && normalizedType === "pte" && (
            <>
              {pteAttempts.length === 0 && <Alert severity="info">No PTE results found for this student.</Alert>}
              <Stack spacing={1.5}>
                {pteAttempts.map((attempt, index) => {
                  const attemptId = getAttemptId(attempt);
                  return (
                    <Card key={`${attemptId || index}-${index}`} sx={{ borderRadius: 2, boxShadow: "0 12px 32px rgba(35,25,66,0.08)" }}>
                      <CardContent>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 850 }}>{getMockTestName(attempt)}</Typography>
                            <Typography sx={{ color: "#6B6478", fontSize: 13 }}>
                              Attempt ID {attemptId || "-"} • {attempt?.TestType || "Full"}
                            </Typography>
                          </Box>
                          <Button variant="contained" disabled={!attemptId} onClick={() => openResult(getPteRoute(attempt, studentId))}>
                            Open
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default TeacherStudentResults;
